import { useMemo } from 'react';

/**
 * Extracts the tenant subdomain from the current browser hostname, accounting for dev patterns.
 *
 * @returns Subdomain string when present; otherwise `null`.
 */
export function useSubdomain(): string | null {
  return useMemo(() => {
    const hostname = window.location.hostname;
    const hostParts = hostname.split('.');
    
    // Check if we have a subdomain
    // For localhost, we check for pattern like "test.localhost"
    if (hostname.includes('localhost')) {
      // Handle .localhost domains (e.g., "test.localhost")
      if (hostParts.length >= 2 && hostParts[1] === 'localhost') {
        const subdomain = hostParts[0];
        // Skip common non-tenant subdomains
        if (['www', 'api', 'admin', 'app'].includes(subdomain)) {
          return null;
        }
        // Strip "dev-" prefix if present
        const cleanSubdomain = subdomain.startsWith('dev-') ? subdomain.substring(4) : subdomain;
        return cleanSubdomain;
      }
      return null;
    }
    
    // For production domains (e.g., "test.yourdomain.com")
    // But skip base domains like "dev.genlp.ai"
    if (hostParts.length >= 3) {
      const subdomain = hostParts[0];
      const baseDomain = hostParts.slice(1).join('.');
      
      // Skip common non-tenant subdomains
      if (['www', 'api', 'admin', 'app'].includes(subdomain)) {
        return null;
      }
      
      // Skip base domain patterns - treat "dev.genlp.ai" as base domain, not subdomain
      if (baseDomain === 'genlp.ai' && subdomain === 'dev') {
        return null;
      }
      
      // Strip "dev-" prefix if present
      const cleanSubdomain = subdomain.startsWith('dev-') ? subdomain.substring(4) : subdomain;
      return cleanSubdomain;
    }
    
    return null;
  }, []);
}

/**
 * Indicates whether the current request is served from a tenant subdomain.
 *
 * @returns `true` when a subdomain is detected, otherwise `false`.
 */
export function useIsSubdomain(): boolean {
  const subdomain = useSubdomain();
  return subdomain !== null;
}
